import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { studioActions, sel } from './store/studioStore';
import { createStudioApi } from './api/studioApi';
import { useSave } from './hooks/useSave';
import { useKeyboard } from './hooks/useKeyboard';
import { useAlert } from './hooks/useAlert';
import { useHeaderSlot } from '../../contexts/HeaderSlotContext';
import {
  fileToDataURL,
  loadImage,
  exportPdfsZip,
  downloadBlob,
} from './utils/canvasUtils';
import Header from './components/Header/Header';
import Canvas from './components/Canvas/Canvas';
import UnifiedSidebar from './components/UnifiedSidebar/UnifiedSidebar';
import {
  NewDocModal,
  PreviewModal,
  BackgroundModal,
  PdfPickerModal,
  DatabaseModal,
  DocumentsModal,
  ForkModal,
} from './components/Modals/Modals';
import useApiContext from 'hooks/useApiContext';

/**
 * <StudioPro />
 *
 * Props:
 *   token       {string}   Bearer token de l'utilisateur connecté
 *   apiBaseUrl  {string}   URL de base de la REST API
 *   eventId     {string}   ID de l'événement (requis pour les API scoped)
 *   documentId  {string}   ID d'un document à charger au montage (optionnel)
 *   onDocumentSaved {fn}   Callback (document) => void après chaque sauvegarde
 */
export default function StudioPro({
  token,
  apiBaseUrl,
  eventId,
  documentId: initialDocId,
}) {
  const dispatch = useDispatch();

  // ── Alert hook ──────────────────────────────────────────────────────────────
  const { showAlert, AlertComponent } = useAlert();

  // ── Claim the app-header center slot ────────────────────────────────────────
  const { claimSlot, releaseSlot } = useHeaderSlot();
  const [slotEl, setSlotEl] = useState(null);
  useEffect(() => {
    claimSlot();
    const raf = requestAnimationFrame(() => {
      setSlotEl(document.getElementById('app-header-center-slot'));
    });
    return () => {
      cancelAnimationFrame(raf);
      setSlotEl(null);
      releaseSlot();
    };
  }, [claimSlot, releaseSlot]);

  // ── API instance ────────────────────────────────────────────────────────────
  const api = useMemo(() => {
    if (!token || !apiBaseUrl || !eventId) return null;
    return createStudioApi({ baseUrl: apiBaseUrl, token, eventId });
  }, [token, apiBaseUrl, eventId]);

  // ── Save ────────────────────────────────────────────────────────────────────
  const { scheduleSave, saveToServer } = useSave(api, eventId);

  // ── Init ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const initializeStudio = async () => {
      if (!api) {
        dispatch(studioActions.pushHistory());
        return;
      }

      try {
        // If a specific document ID is provided, load that document
        if (initialDocId) {
          const doc = await api.getDocument(initialDocId);
          dispatch(
            studioActions.loadDocumentContent({
              ...doc.content,
              documentId: doc.id,
              documentName: doc.name,
            })
          );
          dispatch(studioActions.pushHistory());
          return;
        }

        // Otherwise, try to fetch existing documents for this event
        const documents = await api.getDocuments();

        if (documents && documents.length > 0) {
          // Load the most recent document (assuming they're sorted by date)
          const latestDoc = documents[0];
          const doc = await api.getDocument(latestDoc.id);
          dispatch(
            studioActions.loadDocumentContent({
              ...doc.content,
              documentId: doc.id,
              documentName: doc.name,
            })
          );
          dispatch(studioActions.pushHistory());
        } else {
          // No documents exist, create a new default document
          const defaultContent = {
            doc: {
              widthMm: 210,
              widthPx: 794,
              heightMm: 297,
              heightPx: 1123,
            },
            pages: [{ id: 'page-1', elements: [], background: null }],
            showCropMarks: false,
            db: {
              currentRecord: 0,
              fields: [],
              records: [],
            },
          };

          const newDoc = await api.createDocument(
            'New Document',
            defaultContent
          );
          dispatch(
            studioActions.loadDocumentContent({
              ...defaultContent,
              documentId: newDoc.id,
              documentName: newDoc.name,
            })
          );
          dispatch(studioActions.pushHistory());
        }
      } catch (error) {
        console.error('Failed to initialize from database:', error);
        // Fallback: create empty document in memory only
        dispatch(studioActions.pushHistory());
      }
    };

    initializeStudio();
  }, [api, initialDocId, dispatch]); // Dependency on api and initialDocId

  // ── Zoom handler (shared with keyboard hook) ────────────────────────────────
  const handleZoom = useCallback((z, cx, cy) => {
    window.__studioApplyZoom?.(z, cx, cy);
  }, []);

  // ── Keyboard ────────────────────────────────────────────────────────────────
  useKeyboard(handleZoom, scheduleSave);

  // ── Modals state ────────────────────────────────────────────────────────────
  const [modal, setModal] = useState(null); // 'newDoc'|'preview'|'bg'|'pdfPicker'|'db'|'docs'|'fork'|'products'
  const [forkPage, setForkPage] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfFile, setPdfFile] = useState('');

  const documentId = useSelector(sel.documentId);
  const documentName = useSelector(sel.documentName);
  const pages = useSelector(sel.pages);
  const isFullscreen = useSelector(sel.isFullscreen);
  const doc = useSelector(sel.doc);
  const db = useSelector(sel.db);
  const showCropMarks = useSelector(sel.showCropMarks);

  // ── File inputs (hidden) ────────────────────────────────────────────────────
  const imageInputRef = useRef();
  const bgInputRef = useRef();

  const handleImageFile = async (file) => {
    if (!file) return;
    const src = await fileToDataURL(file);
    const img = await loadImage(src);
    dispatch(
      studioActions.addElement({
        type: 'image',
        extra: {
          src,
          w: 220,
          h: 160,
          naturalW: img.naturalWidth,
          naturalH: img.naturalHeight,
          fill: 'transparent',
          stroke: 'transparent',
          strokeWidth: 0,
          name: file.name,
        },
      })
    );
    dispatch(studioActions.pushHistory());
    scheduleSave();
  };

  const handleBgFile = async (file) => {
    if (!file) return;
    const isPdf =
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf');
    if (isPdf) {
      const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');
      GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

      const ab = await file.arrayBuffer();
      const pdf = await getDocument({ data: ab }).promise;

      if (pdf.numPages === 1) {
        await importPdfPage(pdf, 1, file.name);
      } else {
        setPdfDoc(pdf);
        setPdfFile(file.name);
        setModal('pdfPicker');
      }
    } else {
      const src = await fileToDataURL(file);
      dispatch(
        studioActions.updateBackground({
          kind: 'image',
          src,
          opacity: 1,
          mode: 'contain',
          offsetX: 0,
          offsetY: 0,
          scale: 100,
          locked: false,
          visible: true,
        })
      );
      dispatch(studioActions.pushHistory());
      scheduleSave();
    }
  };

  const importPdfPage = async (pdf, pageNum, fileName) => {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    const src = canvas.toDataURL('image/jpeg', 0.92);
    dispatch(
      studioActions.updateBackground({
        kind: 'image',
        src,
        opacity: 1,
        mode: 'contain',
        offsetX: 0,
        offsetY: 0,
        scale: 100,
        locked: false,
        visible: true,
        name: fileName,
      })
    );
    dispatch(studioActions.pushHistory());
    scheduleSave();
    setModal(null);
  };

  // ── Export PDF (generated server-side) ──────────────────────────────────────
  // Export Print / Export Share both download a PDF rendered by the backend
  // from the event's saved document template, populated with the real
  // showroom-product data returned by the {basePath}/events/{eventId}/
  // showroom-products-preview API (same dbData, same dompdf pipeline used by
  // eventShowroomProducts) — no client-side canvas rendering involved.
  // Export Print keeps the 10mm white margin around the artwork; Export
  // Share bleeds the design to the page edges (whiteSpace = false).
  const requestDocumentPdf = async (name, whiteSpace) => {
    try {
      const response = await exhibitorRepository.exportDocumentPdf(
        eventId,
        name,
        whiteSpace,
        documentId
      );
      downloadBlob(response.data, `${name}.pdf`);
    } catch (e) {
      showAlert({
        title: 'Erreur',
        message: e.message || 'Erreur lors de la génération du PDF',
        type: 'error',
      });
    }
  };

  const handleExportPdf = async () => {
    const name = prompt(
      'Nom du fichier PDF',
      documentName || 'studio-pro-export'
    );
    if (name === null) return;
    await requestDocumentPdf(name, true);
  };

  const exportPdfWithoutWhiteSpace = async () => {
    const name = prompt(
      'Nom du fichier PDF',
      documentName
        ? documentName + '-no-white-space'
        : 'studio-pro-export-no-white-space'
    );
    if (name === null) return;
    await requestDocumentPdf(name, false);
  };
  const { exhibitorRepository } = useApiContext();

  const exportZip = async () => {
    const name = prompt(
      'Nom du fichier ZIP',
      documentName ? documentName + '-database' : 'studio-pro-export-database'
    );
    if (name === null) return;
    console.log('Exporting ZIP with', {
      pages,
      doc,
      db,
      showCropMarks,
      name,
      eventId,
    });
    await exportPdfsZip(
      pages,
      doc,
      db,
      showCropMarks,
      name,
      eventId,
      exhibitorRepository
    );
  };

  // ── Load document from server ────────────────────────────────────────────────
  const handleLoadDocument = useCallback(
    async (id) => {
      if (!api) return;
      try {
        const d = await api.getDocument(id);
        dispatch(
          studioActions.loadDocumentContent({
            ...d.content,
            documentId: d.id,
            documentName: d.name,
          })
        );
        dispatch(studioActions.pushHistory());
      } catch (e) {
        showAlert({
          title: 'Erreur',
          message: 'Erreur chargement : ' + e.message,
          type: 'error',
        });
      }
    },
    [api, dispatch, showAlert]
  );

  return (
    <div className={`sp-app${isFullscreen ? ' sp-fullscreen' : ''}`}>
      {/* Portal Header controls into the app header center slot */}
      {slotEl &&
        ReactDOM.createPortal(
          <Header
            onNewDoc={() => setModal('newDoc')}
            onPreview={() => setModal('preview')}
            onExportPdf={handleExportPdf}
            onExportPdfWithoutWhiteSpace={exportPdfWithoutWhiteSpace}
            onExportZip={exportZip}
            onOpenDocs={() => setModal('docs')}
            onOpenDb={() => setModal('db')}
            scheduleSave={scheduleSave}
            onManualSave={saveToServer}
          />,
          slotEl
        )}

      <Canvas scheduleSave={scheduleSave} />

      <UnifiedSidebar
        onAddImage={() => imageInputRef.current.click()}
        onOpenBg={() => setModal('bg')}
        onForkPage={(page) => {
          setForkPage(page);
          setModal('fork');
        }}
        scheduleSave={scheduleSave}
        showAlert={showAlert}
      />

      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          handleImageFile(e.target.files[0]);
          e.target.value = '';
        }}
      />
      <input
        ref={bgInputRef}
        type="file"
        accept="image/*,.pdf"
        hidden
        onChange={(e) => {
          handleBgFile(e.target.files[0]);
          e.target.value = '';
        }}
      />

      {/* Modals */}
      <NewDocModal
        open={modal === 'newDoc'}
        onClose={() => setModal(null)}
        scheduleSave={scheduleSave}
      />
      <PreviewModal
        open={modal === 'preview'}
        onClose={() => setModal(null)}
        onExportPdf={handleExportPdf}
        onExportPdfWithoutWhiteSpace={exportPdfWithoutWhiteSpace}
      />
      <BackgroundModal
        open={modal === 'bg'}
        onClose={() => setModal(null)}
        onImport={() => bgInputRef.current.click()}
        scheduleSave={scheduleSave}
      />
      <DatabaseModal
        open={modal === 'db'}
        onClose={() => setModal(null)}
        scheduleSave={scheduleSave}
        showAlert={showAlert}
      />
      <DocumentsModal
        open={modal === 'docs'}
        onClose={() => setModal(null)}
        api={api}
        onLoad={handleLoadDocument}
        onNewDoc={() => setModal('newDoc')}
      />
      <ForkModal
        open={modal === 'fork'}
        onClose={() => setModal(null)}
        page={forkPage}
        api={api}
        documentId={documentId}
        documentName={documentName}
        showAlert={showAlert}
      />
      <PdfPickerModal
        open={modal === 'pdfPicker'}
        onClose={() => setModal(null)}
        pdfDoc={pdfDoc}
        fileName={pdfFile}
        onSelect={(i) => importPdfPage(pdfDoc, i, pdfFile)}
      />

      <AlertComponent onSelect={(i) => importPdfPage(pdfDoc, i, pdfFile)} />
    </div>
  );
}

StudioPro.propTypes = {
  token: PropTypes.string,
  apiBaseUrl: PropTypes.string,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
